import { computed } from 'vue';
import { DateTime } from 'luxon';
import { useAuth } from '@/composables/firebase/auth';
import { usePayment } from '@/composables/firebase/payment';
import { useDemoAccount } from '@/composables/useDemoAccount';

export function useUpsale() {
  const { user, hasActiveTrial, hasActiveSubscription } = useAuth();
  const { subscription } = usePayment();
  const { canCreateDemoAccount } = useDemoAccount();

  const isInTrial = computed(() => hasActiveTrial.value && !hasActiveSubscription.value);

  const shouldShowTrialUpgradeMessage = computed(() => {
    if (isInTrial.value && user.value?.profile?.trialUntil) {
      const totalTrialDurationInHours = (import.meta.env.VITE_TRIAL_DURATION ?? 3) * 24;
      const remainingTrialDurationInHours = DateTime.fromISO(user.value.profile.trialUntil).diffNow('hours').hours;

      // more than half of the total trial duration already passed
      return (remainingTrialDurationInHours / totalTrialDurationInHours) <= 0.5;
    }

    return false;
  });

  const canSwitchToYearly = computed(() => (
    subscription.value && (subscription.value.period === 'monthly' || !subscription.value.period) && subscription.value.status === 'active'
  ));

  const shouldShowYearlyUpgradeMessage = computed(() => {
    if (user.value?.profile?.seenUpsaleMessages?.includes('yearlyUpgrade')) {
      return false;
    }

    if (canSwitchToYearly.value && subscription.value) {
      // 65 = two max months (31 days each) plus three days padding ("yeah right, I received the invoice a few days ago")
      return Math.abs(DateTime.fromISO(subscription.value.createdAt).diffNow('days').days) > 65;
    }

    return false;
  });

  const shouldShowDemoAccountMessage = computed(() => {
    if (shouldShowTrialUpgradeMessage.value || shouldShowYearlyUpgradeMessage.value) {
      return false;
    }

    if (user.value?.profile?.seenUpsaleMessages?.includes('demoAccount')) {
      return false;
    }

    return canCreateDemoAccount.value;
  });

  return {
    shouldShowTrialUpgradeMessage,
    shouldShowYearlyUpgradeMessage,
    shouldShowDemoAccountMessage,
  };
}
